import type { AdminNomination, AuditEvent, ChangeRequest, DuplicateSuggestion, Notification, PersonApproval } from '../../../domain/types';
import { seedId } from '../../../lib/uuid';
import { daysAgoISO } from '../../../lib/time';
import type { MockDb } from '../MockDb';
import { A, buildAccess, buildAccounts, buildFamily, buildLocations, buildPeople, buildRelationships, FAMILY_ID, P, RELATIONSHIPS } from './people';
import { seedMilestones } from './milestones';
import { seedPlaces } from './places';
import { seedRecipes } from './recipes';
import { seedStayingClose } from './stayingClose';
import { buildStories, S } from './stories';

export { DEMO_ACCESS_CODE, DEMO_INVITE_TOKEN, DEMO_PASSWORD, DEMO_TEMP_CODE } from './people';

/**
 * Which family a fresh database starts with: the demo family (default, and what
 * the tests use), or the real Edri family's skeleton with VITE_FAMILY_SEED=edri.
 */
function familySeed(): string | undefined {
  return (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_FAMILY_SEED;
}

export async function seedDatabase(db: MockDb): Promise<void> {
  if (familySeed() === 'edri') {
    const { seedEdriFamily } = await import('./edriFamily');
    return seedEdriFamily(db);
  }
  const t = db.t;
  t.families.push(buildFamily());
  const { persons, names } = buildPeople();
  t.persons.push(...persons);
  t.person_names.push(...names);
  t.relationships.push(...buildRelationships());
  t.locations.push(...buildLocations());
  seedPlaces(db);
  const { accounts, memberships, links, grants } = buildAccounts();
  t.accounts.push(...accounts);
  t.family_memberships.push(...memberships);
  t.person_account_links.push(...links);
  t.profile_management_grants.push(...grants);
  const { codes, invitations, temp } = buildAccess();
  t.access_codes.push(...codes);
  t.invitations.push(...invitations);
  t.temporary_access_sessions.push(...temp);

  // Who runs the family, mid-conversation. Sarah and Daniel are the two
  // administrators; Sarah has suggested Tom and approved her own suggestion, so
  // it sits at one of two, waiting for Daniel — sign in as him to carry it, or
  // leave it and watch the no-objection timeout do the work.
  const nominations: AdminNomination[] = [
    {
      id: seedId('nomination', 'tom'),
      familyId: FAMILY_ID,
      email: 'tom@edry.example',
      accountId: A('tom'),
      nominatedByAccountId: A('sarah'),
      reason: 'He already looks after Gideon’s profile and knows everyone.',
      votes: [{ accountId: A('sarah'), vote: 'approve', at: daysAgoISO(3) }],
      status: 'open',
      createdAt: daysAgoISO(3),
    },
  ];
  t.admin_nominations.push(...nominations);

  // The duplicate "David Edri" was added by a new member and is still dimmed:
  // whoever adds someone counts as the first to recognise them, so it needs one
  // more relative (or any administrator) to become a full circle.
  const approvals: PersonApproval[] = [
    { id: seedId('approval', 'david-dup'), familyId: FAMILY_ID, personId: P('david_dup'), accountId: A('yonatan'), byAdmin: false, createdAt: daysAgoISO(2) },
  ];
  t.person_approvals.push(...approvals);

  const st = await buildStories();
  t.stories.push(...st.stories);
  t.story_contributions.push(...st.contributions);
  // Voice notes the family left on a story's day — Moments like any other.
  t.family_photos.push(...st.photos);
  t.story_people.push(...st.storyPeople);
  t.events.push(...st.events);
  t.story_events.push(...st.storyEvents);
  t.media_assets.push(...st.media);
  t.upload_sessions.push(...st.uploads);
  t.transcripts.push(...st.transcripts);
  t.recording_drafts.push(...st.drafts);
  t.story_suggestions.push(...st.suggestions);

  // ---- review queue --------------------------------------------------------
  const pendingRelIndex = RELATIONSHIPS.findIndex((r) => r.status === 'pending');
  const pendingRelId = seedId('rel', `${pendingRelIndex}:dalia:eitan`);
  const crs: ChangeRequest[] = [
    {
      id: seedId('cr', 'eitan-dalia'), familyId: FAMILY_ID, kind: 'relationship_add',
      payload: { relationshipId: pendingRelId, type: 'parent_child', fromPersonId: P('dalia'), toPersonId: P('eitan'), subtype: 'biological' },
      summary: 'Add Dalia Cohen as Eitan’s mother', summaryHe: 'הוספת דליה כהן כאמו של איתן', requesterAccountId: A('eitan'),
      reason: 'My birth mother is Dalia, not Ruth. Ruth raised me from age 4.', status: 'pending', targetEntityType: 'relationship', targetEntityId: pendingRelId,
      createdAt: daysAgoISO(2), updatedAt: daysAgoISO(2),
    },
    {
      id: seedId('cr', 'yosef-birth'), familyId: FAMILY_ID, kind: 'person_field',
      payload: { personId: P('yosef'), field: 'birth', from: { kind: 'year', start: '1935' }, to: { kind: 'year', start: '1936' } },
      summary: 'Change Yosef’s birth year from 1935 to 1936', summaryHe: 'שינוי שנת הלידה של יוסף מ-1935 ל-1936', requesterAccountId: A('daniel'),
      reason: 'His ID card says 1936.', status: 'question', reviewerAccountId: A('david'), reviewerQuestion: 'Could you photograph the ID card? The passport we have says 1935.',
      targetEntityType: 'person', targetEntityId: P('yosef'), createdAt: daysAgoISO(6), updatedAt: daysAgoISO(4),
    },
    {
      id: seedId('cr', 'yonatan-claim'), familyId: FAMILY_ID, kind: 'claim',
      payload: { accountId: A('yonatan'), personId: P('yonatan') },
      summary: 'yoni.m@example.com claims to be Yonatan Mizrahi', summaryHe: 'yoni.m@example.com מבקש שיוך ליונתן מזרחי', requesterAccountId: A('yonatan'),
      status: 'pending', targetEntityType: 'person', targetEntityId: P('yonatan'), createdAt: daysAgoISO(2), updatedAt: daysAgoISO(2),
    },
    {
      id: seedId('cr', 'david-dup-person'), familyId: FAMILY_ID, kind: 'person_add',
      payload: { personId: P('david_dup') },
      summary: 'New person “David Edri” (about 1955) awaiting review', summaryHe: 'אדם חדש "דוד אדרי (Edri)" (בערך 1955) ממתין לבדיקה', requesterAccountId: A('daniel'),
      reason: 'My father’s cousin', status: 'pending', targetEntityType: 'person', targetEntityId: P('david_dup'), createdAt: daysAgoISO(1), updatedAt: daysAgoISO(1),
    },
    {
      id: seedId('cr', 'approved-example'), familyId: FAMILY_ID, kind: 'relationship_add',
      payload: { type: 'parent_child', fromPersonId: P('gideon'), toPersonId: P('tom'), subtype: 'biological' },
      summary: 'Add Gideon Katz as Tom’s father', summaryHe: 'הוספת גדעון כץ כאביו של תום', requesterAccountId: A('tom'), status: 'approved',
      reviewerAccountId: A('sarah'), resolution: 'Approved', targetEntityType: 'relationship', createdAt: daysAgoISO(31), updatedAt: daysAgoISO(30),
    },
  ];
  t.change_requests.push(...crs);

  // ---- shared phone numbers + "ask them something": one open question waiting for Savta Miriam ----
  seedStayingClose(db);
  seedMilestones(db);
  // ---- the family's kitchen: two recipes to read, and one of Sarah's still being written ----
  seedRecipes(db);

  const dups: DuplicateSuggestion[] = [
    { id: seedId('dup', 'david'), familyId: FAMILY_ID, personAId: P('david'), personBId: P('david_dup'), confidence: 0.86, reason: 'Same given name, family name differs by one letter, birth year within 1 year', status: 'open', createdAt: daysAgoISO(1) },
  ];
  t.duplicate_suggestions.push(...dups);

  // ---- audit history ---------------------------------------------------------
  const audit: Array<Omit<AuditEvent, 'id' | 'familyId'>> = [
    { actorAccountId: A('sarah'), entityType: 'family', entityId: FAMILY_ID, action: 'create', description: 'Family space created', timestamp: daysAgoISO(400) },
    { actorAccountId: A('sarah'), entityType: 'person', entityId: P('yaakov'), action: 'lock', description: 'Locked names and dates: verified against the family record book', timestamp: daysAgoISO(200) },
    { actorAccountId: A('sarah'), entityType: 'account', entityId: A('david'), action: 'role_change', before: { role: 'member' }, after: { role: 'editor' }, description: 'David Edry became a Family Editor', timestamp: daysAgoISO(180) },
    { actorAccountId: A('sarah'), entityType: 'grant', entityId: seedId('grant', 'tom-gideon'), action: 'grant', description: 'Tom Katz may manage Gideon Katz’s profile', timestamp: daysAgoISO(30) },
    { actorAccountId: A('sarah'), entityType: 'change_request', entityId: seedId('cr', 'approved-example'), action: 'approve', description: 'Approved: Add Gideon Katz as Tom’s father', timestamp: daysAgoISO(30), approvalStatus: 'approved' },
    { actorAccountId: A('david'), entityType: 'person', entityId: P('miriam'), action: 'update', before: { birth: { kind: 'year', start: '1932' } }, after: { birth: { kind: 'exact', start: '1932-04-18', certainty: 'confirmed' } }, description: 'Birth date made exact from Miriam’s passport', entityVersion: 1, timestamp: daysAgoISO(25) },
    { actorAccountId: A('sarah'), entityType: 'story', entityId: S('aliyah'), action: 'publish', description: 'Published “How Saba Moshe came to Israel” (spoken by Miriam, uploaded by Sarah)', onBehalfOfPersonId: P('miriam'), timestamp: daysAgoISO(2, 3) },
    { actorAccountId: A('sarah'), entityType: 'access', entityId: seedId('temp', 'miriam-oldphone'), action: 'revoke', description: 'Revoked temporary device access: Old phone (lost)', timestamp: daysAgoISO(60) },
    { actorAccountId: A('david'), entityType: 'access', entityId: seedId('inv', 'nathalie'), action: 'create', description: 'Invitation sent to nathalie@example.com for Nathalie Edry-Dubois', timestamp: daysAgoISO(1) },
    { actorAccountId: A('sarah'), entityType: 'access', entityId: seedId('code', '2025'), action: 'revoke', description: 'Family access code EDRY-2025 revoked', timestamp: daysAgoISO(200) },
    // Recent, so Daniel counts as an active administrator and a majority is genuinely two.
    { actorAccountId: A('daniel'), entityType: 'person', entityId: P('shimon'), action: 'update', description: 'Tidied the spelling of Shimon’s Hebrew name', timestamp: daysAgoISO(5) },
  ];
  for (const a of audit) db.audit(a);

  // ---- notifications ---------------------------------------------------------
  const notes: Array<Omit<Notification, 'id' | 'read' | 'createdAt'> & { daysAgo: number; read?: boolean }> = [
    { accountId: A('sarah'), kind: 'review', title: '4 items are waiting for review', titleHe: '4 פריטים ממתינים לבדיקה', link: '/manage', daysAgo: 1 },
    { accountId: A('sarah'), kind: 'story', title: 'David added a recording to “Grandpa’s workshop”', titleHe: 'דוד הוסיף הקלטה ל"הנגרייה של סבא"', link: `/stories/${S('workshop')}`, daysAgo: 5 },
    { accountId: A('david'), kind: 'upload', title: 'Your recording for “Grandpa’s workshop” is uploaded; backup pending', titleHe: 'ההקלטה שלך ל"הנגרייה של סבא" הועלתה; הגיבוי ממתין', link: `/stories/${S('workshop')}`, daysAgo: 1 },
    { accountId: A('david'), kind: 'review', title: 'Eitan asked to add Dalia Cohen as his mother', titleHe: 'איתן ביקש להוסיף את דליה כהן כאמו', link: '/manage', daysAgo: 2 },
    { accountId: A('david'), kind: 'review', title: 'Possible duplicate: David Edri / David Edry', titleHe: 'כפילות אפשרית: David Edri / דוד אדרי', link: '/manage/duplicates', daysAgo: 1 },
    { accountId: A('eitan'), kind: 'approval', title: 'Your correction is waiting for a Family Editor', titleHe: 'התיקון שלך ממתין לעורך משפחה', daysAgo: 2 },
    { accountId: A('daniel'), kind: 'approval', title: 'David asked a question about Yosef’s birth year', titleHe: 'דוד שאל שאלה על שנת הלידה של יוסף', link: '/me', daysAgo: 4 },
    { accountId: A('noa'), kind: 'upload', title: 'Your recording is paused — it will retry when you are online', titleHe: 'ההקלטה שלך מושהית — תנסה שוב כשיהיה חיבור', link: `/stories/${S('passover')}`, daysAgo: 6 },
  ];
  for (const n of notes) {
    const { daysAgo, read, ...rest } = n;
    t.notifications.push({ id: seedId('notif', `${n.accountId}:${n.title}`), read: read ?? false, createdAt: daysAgoISO(daysAgo), ...rest });
  }
}
